import { CoreApplicationContext } from '@eventbrite/context-gen';
import { LocalizationContextProvider } from '@eventbrite/create-common';
import { initializeErrorReporting } from '@eventbrite/error-reporting';
import {
    getLocaleOverride,
    getParentLocale,
    setupAsync,
} from '@eventbrite/i18n';
import { initDimensionsFromState } from '@eventbrite/site-analytics';
import { QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { render } from 'react-dom';
import { App } from './App';
import { AppContextProvider } from './AppContext';
import { DraftEventProvider } from './DraftEventStoreProvider';
import { initDatadogRUM } from './lib/initDatadogRUM';
import { queryClient } from './queryClient';
import { TestingPanel } from './shared/TestingPanel';

// retrieve data from server to hydrate on the client
const props = window.__SERVER_DATA__ || ({} as CoreApplicationContext);
const { app_name, app_version, env, user } = props;

const parentLocale = getParentLocale(
    getLocaleOverride() || props.env.localeInfo.locale,
);

const main = async () => {
    const envLocale = env?.localeInfo?.locale;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    initializeErrorReporting();
    try {
        if (parentLocale !== 'en_US') {
            await setupAsync({
                parentLocale,
                translations: /* webpackChunkName: "i18n-translation" */ import(
                    `./i18n/translations/${parentLocale}.json`
                ),
            });
        }
    } finally {
        render(
            <QueryClientProvider client={queryClient}>
                <DraftEventProvider>
                    <LocalizationContextProvider
                        initialValue={{
                            locale: envLocale,
                            timezone: {
                                timezone,
                                label: timezone,
                            },
                        }}
                    >
                        <AppContextProvider initialValue={{ user }}>
                            <App {...props} />
                            <TestingPanel />
                        </AppContextProvider>
                    </LocalizationContextProvider>
                </DraftEventProvider>
            </QueryClientProvider>,
            document.getElementById('root'),
        );

        initDatadogRUM({
            app: { name: app_name, version: app_version },
            env,
        });
    }
};

main();

initDimensionsFromState(props);
